// --- React and Redux
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
// --- Actions and Hooks
import {useClearanceMemoTitlesActions} from '../../../../store/clearanceMemoTitles/useClearanceMemoTitles';
import {useLoading} from '../../../../utils/hooks/useLoading';
// --- UI Components
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import {MultiSelect} from '@dtci/spellbook/MultiSelect';
import Select from '@dtci/spellbook/Select';
import {Col, Row} from 'antd';
import {InputLabel} from '../../../../components/InputLabel/InputLabel';
// --- Utilities
import {datastageApi} from '../../../../api';
import {filterCriteria} from '../../../../utils/filterCriteria';
// --- Styles
import './styles.scss';
import {usePermissions} from "../../../../utils/permissions";

/** ClearanceMemoTitlesFilterForm Component
 *
 * Renders a filter form for clearance memo titles with various input fields and buttons.
 *
 * @returns {JSX.Element} The Clearance Memo Titles Filter Form component.
 */
export function ClearanceMemoTitlesFilterForm() {
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {
        updateClearanceMemoTitles,
        updateClearanceMemoTitlesFilterForm,
        clearClearanceMemoTitles,
    } = useClearanceMemoTitlesActions();
    const {filterForm, legalStatusOptions, importedAndMappedOptions, defaultFilterRange, titleSort} =
        useSelector((state) => state.titles);

    const {isLegalAdmin} = usePermissions();

    // Fetch clearance memo titles
    useEffect(() => {
        const timeoutId = setTimeout(handleFilter, 5);

        // Cleanup function to clear the timeout
        return () => clearTimeout(timeoutId);
    }, []);

    // Fetch clearance memo titles based on filter criteria
    const fetchClearanceMemoTitles = async (filter = {}, range, sort) => {
        try {
            return await datastageApi.clearanceMemoTitles(filter, range, sort);
        } catch (error) {
            console.error('Error fetching clearance memo titles:', error);
            return null;
        }
    };

    // Handle filter button click
    const handleFilter = async () => {
        setLoadingTrue();
        try {
            const data = await fetchClearanceMemoTitles(
                filterCriteria(filterForm, '', 'clearanceMemoTitles'),
                defaultFilterRange,
                titleSort,
            );
            updateClearanceMemoTitles({
                titles: mapLegalStatus(data?.content),
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error filtering clearance memo titles:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle reset button click
    const handleResetFilter = async () => {
        setLoadingTrue();
        try {
            clearClearanceMemoTitles();
            const data = await fetchClearanceMemoTitles({}, defaultFilterRange, titleSort);
            updateClearanceMemoTitles({
                titles: mapLegalStatus(data?.content),
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
                totalItems: data?.totalElements,
            });
        } catch (error) {
            console.error('Error resetting memo title filter:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Map legal status IDs to their corresponding labels
    const mapLegalStatus = (content) =>
        content?.map((item) => ({
            ...item,
            legalStatusId:
                legalStatusOptions.find((lookup) => lookup.value === item?.legalStatusId)?.label ||
                null,
        }));

  
    return (
       <Row className="clearance-memo-titles-filter-form">
            <Col>
                <InputLabel colProps={{span: 24}} label="FOX WPR ID">
                    <Input
                        value={filterForm.foxFinancialTitleId}
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {foxFinancialTitleId: e.target.value},
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="foxFinancialTitleId"
                        name="foxFinancialTitleId"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{span: 24}} label="Title">
                    <Input
                        value={filterForm.title}
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {title: e.target.value},
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="title"
                        name="title"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{span: 24}} label="Product Type">
                    <Input
                        value={filterForm.productType}
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {productType: e.target.value},
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="productType"
                        name="productType"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{span: 24}} label="FOX ID">
                    <Input
                        value={filterForm.foxipediaId}
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {foxipediaId: e.target.value},
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="foxipediaId"
                        name="foxipediaId"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{span: 24}} label="Status">
                    <Input
                        value={filterForm.lifeCycleStatus}
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {lifeCycleStatus: e.target.value},
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="lifeCycleStatus"
                        name="lifeCycleStatus"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{span: 24}} label="Keywords">
                    <Input
                        value={filterForm.keywords}
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {keywords: e.target.value},
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="keywords"
                        name="keywords"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{span: 24}} label="Legal Status">
                    <Select
                        onChange={(value) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'filterForm',
                                data: {legalStatusId: value},
                            })
                        }
                        name="legalStatusId"
                        options={legalStatusOptions}
                        isSearchable
                        isClearable
                        value={filterForm.legalStatusId}
                    />
                </InputLabel>
            </Col>
            {isLegalAdmin && (
                <>
                    <Col>
                        <InputLabel colProps={{span: 24}} label="Mapped">
                            <Select
                                onChange={(value) =>
                                    updateClearanceMemoTitlesFilterForm({
                                        key: 'filterForm',
                                        data: {clearanceMemoMapped: value},
                                    })
                                }
                                name="clearanceMemoMapped"
                                options={importedAndMappedOptions}
                                isSearchable
                                isClearable
                                value={filterForm.clearanceMemoMapped}
                            />
                        </InputLabel>
                    </Col>
                    <Col>
                        <InputLabel colProps={{span: 24}} label="Imported">
                            <Select
                                onChange={(value) =>
                                    updateClearanceMemoTitlesFilterForm({
                                        key: 'filterForm',
                                        data: {clearanceMemoImported: value},
                                    })
                                }
                                name="clearanceMemoImported"
                                options={importedAndMappedOptions}
                                isSearchable
                                isClearable
                                value={filterForm.clearanceMemoImported}
                            />
                        </InputLabel>
                    </Col>
                </>
            )}
            <Col>
                <Button
                    className="filter-button"
                    sizeType="small"
                    styleType="promoted"
                    onClick={handleFilter}
                >
                    Filter
                </Button>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
